import { useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import { getPlayerStreamUrl } from "../api/jellyfin";
import type { Credentials } from "../storage/credentials";
import type { NativePreferences } from "../storage/preferences";
import {
  getDeliveryBadge,
  getPlaybackMethod,
  isManagedHlsSource,
  resolveStreamStartSeconds,
  type ServerSeekIntent,
} from "./streamPlan";
import { resolveResumePositionSeconds } from "./resumePosition";
import { usePlayerEpisodes } from "./usePlayerEpisodes";
import { usePlayerMediaQueries } from "./usePlayerMediaQueries";
import { usePlayerSources } from "./usePlayerSources";
import { usePlayerTracks } from "./usePlayerTracks";

export type PlayerMediaRuntimeInput = {
  credentials: Credentials | null;
  itemId: string;
  explicitSourceId?: string;
  startOver?: boolean;
  selectedSourceId: string;
  setSelectedSourceId: Dispatch<SetStateAction<string>>;
  decoderReady: boolean;
  versionsRequested: boolean;
  preferences: NativePreferences;
  preferencesReady: boolean;
  setPreferences: Dispatch<SetStateAction<NativePreferences>>;
  selectedSeasonId: string;
  setSelectedSeasonId: Dispatch<SetStateAction<string>>;
  allowAutomaticPinnedSelection: boolean;
  serverSeek: ServerSeekIntent | null;
  prepareTrackChange: (
    onlyIfDecoderReady: boolean,
    managedHls: boolean,
  ) => boolean;
};

/** Owns media discovery, ranking, tracks, and the authoritative stream plan. */
export function usePlayerMediaRuntime(input: PlayerMediaRuntimeInput) {
  const {
    itemQuery,
    playbackQuery,
    segmentsQuery,
    probedSources,
  } = usePlayerMediaQueries({
    credentials: input.credentials,
    itemId: input.itemId,
    selectedSourceId: input.selectedSourceId,
    explicitSourceId: input.explicitSourceId,
    decoderReady: input.decoderReady,
    versionsRequested: input.versionsRequested,
  });
  const item = itemQuery.data;
  const resumeSeconds = resolveResumePositionSeconds({
    playbackPositionTicks: item?.UserData?.PlaybackPositionTicks,
    runTimeTicks: item?.RunTimeTicks,
    played: item?.UserData?.Played,
    startOver: input.startOver,
  });
  const episodes = usePlayerEpisodes({
    credentials: input.credentials,
    item,
    selectedSeasonId: input.selectedSeasonId,
    setSelectedSeasonId: input.setSelectedSeasonId,
  });
  const sourceRuntime = usePlayerSources({
    itemSources: item?.MediaSources,
    probedSources,
    selectedSourceId: input.selectedSourceId,
    setSelectedSourceId: input.setSelectedSourceId,
    explicitSourceId: input.explicitSourceId,
    preferenceScope: item?.SeriesId ?? item?.Id ?? "",
    preferredAudioLanguage: input.preferences.audioLanguage,
    allowAutomaticPinnedSelection: input.allowAutomaticPinnedSelection,
  });
  const managedHls = isManagedHlsSource(sourceRuntime.source);
  const streamStartSeconds = resolveStreamStartSeconds({
    managedHls,
    itemId: input.itemId,
    serverSeek: input.serverSeek,
    resumeSeconds,
  });
  const tracks = usePlayerTracks({
    source: sourceRuntime.source,
    preferences: input.preferences,
    preferencesReady: input.preferencesReady,
    setPreferences: input.setPreferences,
    prepareForTrackChange: (onlyIfDecoderReady) => {
      input.prepareTrackChange(onlyIfDecoderReady, managedHls);
    },
  });
  // Audio changes remount the decoder; subtitle changes are client-side
  // only (JASSUB/libbitsub/VTT on web, embedded tracks on mpv).
  const streamUrl =
    input.credentials && item && sourceRuntime.source && playbackQuery.data
      ? getPlayerStreamUrl(input.credentials, input.itemId, sourceRuntime.source, {
          playSessionId: playbackQuery.data.PlaySessionId,
          audioStreamIndex: tracks.selectedAudioIndex,
          startTimeTicks: streamStartSeconds > 0
            ? Math.floor(streamStartSeconds * 10_000_000)
            : undefined,
        })
      : undefined;

  return {
    itemQuery,
    playbackQuery,
    segmentsQuery,
    item,
    resumeSeconds,
    ...episodes,
    ...sourceRuntime,
    managedHls,
    streamStartSeconds,
    streamUrl,
    playMethod: getPlaybackMethod(sourceRuntime.source),
    deliveryBadge: getDeliveryBadge(sourceRuntime.source),
    ...tracks,
  };
}
